import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Button,
  Box,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { get, put } from "../../../utils/api";
import { textError } from "../../../utils/validationMessage";
import {
  annualPolicyYesFieldErrors,
  getEffectiveDateMinMax,
  hasUploadedDocument,
  isAnnualPolicyUploadDisabled,
  shouldShowAnnualPolicyRevised,
} from "../../../utils/annualPolicyFormHelpers";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import WestIcon from "@mui/icons-material/West";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import { useUser } from "../../../context/AuthContext";
import DeleteModal from "../../../Components/Common/DeleteModal";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const removeCommas = (val) => {
  if (val === null || val === undefined) return "";
  return val.toString().replace(/,/g, "").trim();
};

const formatWithCommas = (val) => {
  const normalized = removeCommas(val);
  if (normalized === "") return "";
  if (!/^\d*\.?\d*$/.test(normalized)) return normalized;
  const [integerPart = "", decimalPart] = normalized.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

const stripCommasFromNumericStrings = (input) => {
  if (Array.isArray(input)) return input.map(stripCommasFromNumericStrings);
  if (input && typeof input === "object") {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [
        key,
        stripCommasFromNumericStrings(value),
      ]),
    );
  }
  if (typeof input === "string" && /^\d{1,3}(,\d{3})*(\.\d+)?$/.test(input)) {
    return removeCommas(input);
  }
  return input;
};

const BooleanRadioGroup = ({ label, name, value, onChange }) => (
  <FormControl component="fieldset" style={{ marginBottom: "0.3rem" }}>
    <p
      className={styles.form_heading}
      style={{ fontWeight: "bold", marginTop: "1rem" }}
    >
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value === false || value === "false" ? "false" : "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel
        value="true"
        control={<Radio sx={socialRadioSx} />}
        label="Yes"
      />
      <FormControlLabel
        value="false"
        control={<Radio sx={socialRadioSx} />}
        label="No"
      />
    </RadioGroup>
  </FormControl>
);

const LabourPractices = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const [errors, setErrors] = useState({});
  const [module, setModule] = useState("monthly");
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [entryYear, setEntryYear] = useState(null);
  const todayIso = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();
  const maxEffectiveDate =
    Number(entryYear) && Number(entryYear) < currentYear
      ? `${entryYear}-12-31`
      : todayIso;
  const { min: labourPolicyEffMin, max: labourPolicyEffMax } =
    getEffectiveDateMinMax(entryYear);
  // Store meta needed for dashboard pending API
  const [entryMeta, setEntryMeta] = useState({
    companyId: null,
    year: null,
    month: null,
  });
  // Guard to avoid calling monthlyLaborPracticesPending more than once per logical load
  const hasFetchedPendingRef = useRef(false);
  // Store historical totals coming from monthlyLaborPracticesPending API
  const pendingTotalsRef = useRef({
    totalComplaints: 0,
    totalResolved: 0,
    totalPending: 0,
  });

  // Helper to know current totalPending from dashboard API for rendering/help text
  const currentTotalPending =
    Number(pendingTotalsRef.current?.totalPending) || 0;

  // Annual formData
  const annualFormDataInitial = {
    labourLawCompliance: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    noChildLabourPolicy: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    noForcedLabourPolicy: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    collectiveBargainingAgreements: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    freedomOfAssociation: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    assessments: [
      {
        type: "(a) Child Labour",
        facilitiesAssessed: "",
        percentageAssessed: "",
      },
      {
        type: "(b) Forced Labour",
        facilitiesAssessed: "",
        percentageAssessed: "",
      },
      {
        type: "(c) Sexual Harassment",
        facilitiesAssessed: "",
        percentageAssessed: "",
      },
      {
        type: "(d) Discrimination at workplace",
        facilitiesAssessed: "",
        percentageAssessed: "",
      },
      { type: "(e) Wages", facilitiesAssessed: "", percentageAssessed: "" },
      { type: "(f) Others", facilitiesAssessed: "", percentageAssessed: "" },
    ],
    legalMinimumWageCompliance: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
  };

  // Monthly formData
  const monthlyFormDataInitial = {
    labourComplaints: "",
    labourComplaintsResolved: "",
    labourComplaintsPending: "",
    labourComplaintsResolutionRate: "",
    POSH: {
      status: true,
      documentUrl: "",
      filed: "",
      resolved: "",
      pending: "",
    },
  };

  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);
  const [monthlyFormData, setMonthlyFormData] = useState(
    monthlyFormDataInitial,
  );
  const [previousPolicyDocuments, setPreviousPolicyDocuments] = useState({
    labourLawCompliance: "",
    noChildLabourPolicy: "",
    noForcedLabourPolicy: "",
    collectiveBargainingAgreements: "",
    freedomOfAssociation: "",
    legalMinimumWageCompliance: "",
  });
  const [deletedPolicyDocs, setDeletedPolicyDocs] = useState({
    labourLawCompliance: false,
    noChildLabourPolicy: false,
    noForcedLabourPolicy: false,
    collectiveBargainingAgreements: false,
    freedomOfAssociation: false,
    legalMinimumWageCompliance: false,
  });
  const [previousPolicyMeta, setPreviousPolicyMeta] = useState({
    labourLawCompliance: { remark: "", effectiveDate: "" },
    noChildLabourPolicy: { remark: "", effectiveDate: "" },
    noForcedLabourPolicy: { remark: "", effectiveDate: "" },
    collectiveBargainingAgreements: { remark: "", effectiveDate: "" },
    freedomOfAssociation: { remark: "", effectiveDate: "" },
    legalMinimumWageCompliance: { remark: "", effectiveDate: "" },
  });
  const [previousYearPolicyDocuments, setPreviousYearPolicyDocuments] = useState({
    labourLawCompliance: "",
    noChildLabourPolicy: "",
    noForcedLabourPolicy: "",
    collectiveBargainingAgreements: "",
    freedomOfAssociation: "",
    legalMinimumWageCompliance: "",
  });
  const [facilities, setFacilities] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteAssessmentIndex, setDeleteAssessmentIndex] = useState(null);
  const predefinedAssessmentTypes = [
    "(a) Child Labour",
    "(b) Forced Labour",
    "(c) Sexual Harassment",
    "(d) Discrimination at workplace",
    "(e) Wages",
  ];

  // Keep assessment serials stable: predefined (a-e), custom (f...),
  // and "Others" always as the last row with the next serial.
  const rebuildAssessments = useCallback(
    (assessments = []) => {
      const predefinedRows = [];
      const customRows = [];
      let othersRow = null;

      assessments.forEach((assessment) => {
        const assessmentType = assessment?.type || "";
        if (assessmentType.includes("Others")) {
          othersRow = assessment;
        } else if (predefinedAssessmentTypes.includes(assessmentType)) {
          predefinedRows.push(assessment);
        } else {
          customRows.push(assessment);
        }
      });

      const relabeledCustomRows = customRows.map((row, customIndex) => {
        const customLetter = String.fromCharCode(102 + customIndex); // f, g, h...
        return {
          ...row,
          type: `(${customLetter})`,
        };
      });

      const othersLetter = String.fromCharCode(
        102 + relabeledCustomRows.length,
      );
      const othersLabel = `(${othersLetter}) Others`;

      return [
        ...predefinedRows,
        ...relabeledCustomRows,
        othersRow
          ? { ...othersRow, type: othersLabel }
          : {
              type: othersLabel,
              facilitiesAssessed: "",
              percentageAssessed: "",
            },
      ];
    },
    [predefinedAssessmentTypes],
  );

  // Get the appropriate formData based on module
  const formData = module === "annual" ? annualFormData : monthlyFormData;
  const setFormData =
    module === "annual" ? setAnnualFormData : setMonthlyFormData;

  const parseStatusValue = (status, defaultValue = true) => {
    if (status === undefined || status === null) return defaultValue;
    if (typeof status === "boolean") return status;
    if (typeof status === "string") {
      const normalized = status.trim().toLowerCase();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
      if (normalized === "yes") return true;
      if (normalized === "no") return false;
    }
    return Boolean(status);
  };

  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    const validateRecursive = (data, errorsObj) => {
      Object.entries(data).forEach(([key, value]) => {
        if (key === "percentageAssessed") {
          // Auto-calculated field; do not block submit with manual validation.
          return;
        }

        if (Array.isArray(value)) {
          // Keep original indices so UI errors map to the correct assessment row.
          // "Others" row is intentionally excluded from validation.
          const arrayErrors = [];

          value.forEach((item, index) => {
            if (
              key === "assessments" &&
              item?.type &&
              item.type.includes("Others")
            ) {
              return;
            }

            if (typeof item === "object") {
              const nestedErrors = {};
              validateRecursive(item, nestedErrors);
              if (Object.keys(nestedErrors).length > 0) {
                arrayErrors[index] = nestedErrors;
                hasErrors = true;
              }
            } else if (isEmpty(item)) {
              arrayErrors[index] = textError;
              hasErrors = true;
            }
          });

          if (arrayErrors.some(Boolean)) {
            errorsObj[key] = arrayErrors;
          }
        } else if (typeof value === "object" && value !== null) {
          let nestedErrors = {};

          const annualLabourPolicyKeys = [
            "labourLawCompliance",
            "noChildLabourPolicy",
            "noForcedLabourPolicy",
            "collectiveBargainingAgreements",
            "freedomOfAssociation",
            "legalMinimumWageCompliance",
          ];
          if (annualLabourPolicyKeys.includes(key)) {
            // Yes-path file/remark/effectiveDate validated after validateRecursive (annual module)
          } else if (key === "POSH") {
            if (value.status) {
              if (isEmpty(value.documentUrl)) {
                nestedErrors.documentUrl = textError;
                hasErrors = true;
              }
              if (isEmpty(value.filed)) {
                nestedErrors.filed = textError;
                hasErrors = true;
              }
              if (isEmpty(value.resolved)) {
                nestedErrors.resolved = textError;
                hasErrors = true;
              }
              if (isEmpty(value.pending)) {
                nestedErrors.pending = textError;
                hasErrors = true;
              }
            }
          } else {
            validateRecursive(value, nestedErrors);
          }
          if (Object.keys(nestedErrors).length > 0) {
            errorsObj[key] = nestedErrors;
            hasErrors = true;
          }
        } else if (isEmpty(value)) {
          errorsObj[key] = textError;
          hasErrors = true;
        }
      });
    };

    // Only validate fields relevant to the current module
    if (module === "annual") {
      validateRecursive(formData, newErrors);

      const annualPolicyKeys = [
        "labourLawCompliance",
        "noChildLabourPolicy",
        "noForcedLabourPolicy",
        "collectiveBargainingAgreements",
        "freedomOfAssociation",
        "legalMinimumWageCompliance",
      ];
      annualPolicyKeys.forEach((key) => {
        if (formData[key]?.status === false) {
          delete newErrors[key];
          return;
        }
        const v = formData[key];
        if (!v?.status) return;
        const showRev = shouldShowAnnualPolicyRevised(
          !isFirstEntry,
          previousYearPolicyDocuments[key],
        );
        const pe = annualPolicyYesFieldErrors(v, entryYear, showRev);
        if (Object.keys(pe).length > 0) {
          newErrors[key] = {
            ...(typeof newErrors[key] === "object" && newErrors[key]
              ? newErrors[key]
              : {}),
            ...pe,
          };
          hasErrors = true;
        }
      });
    } else {
      // For monthly module, validate monthly fields
      validateRecursive(formData, newErrors);

      // Remove errors if POSH status is false
      if (!parseStatusValue(formData.POSH?.status, true)) {
        delete newErrors.POSH;
      } else {
        if (!newErrors.POSH) newErrors.POSH = {};
        if (isEmpty(formData?.POSH?.documentUrl)) {
          newErrors.POSH.documentUrl = "This field is required";
          hasErrors = true;
        }
        if (isEmpty(formData?.POSH?.filed)) {
          newErrors.POSH.filed = "This field is required";
          hasErrors = true;
        }
        if (isEmpty(formData?.POSH?.resolved)) {
          newErrors.POSH.resolved = "This field is required";
          hasErrors = true;
        }
        if (isEmpty(formData?.POSH?.pending)) {
          newErrors.POSH.pending = "This field is required";
          hasErrors = true;
        }
      }

      if (isEmpty(formData?.labourComplaintsPending)) {
        newErrors.labourComplaintsPending = textError;
        hasErrors = true;
      }
    }

    setErrors(newErrors);
    return hasErrors;
  };
  const annualLabourPolicyKeys = [
    "labourLawCompliance",
    "noChildLabourPolicy",
    "noForcedLabourPolicy",
    "collectiveBargainingAgreements",
    "freedomOfAssociation",
    "legalMinimumWageCompliance",
  ];

  const handleFileChange = (file, policy) => {
    if (annualLabourPolicyKeys.includes(policy)) {
      setDeletedPolicyDocs((prev) => ({
        ...prev,
        [policy]: !hasUploadedDocument(file),
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        documentUrl: file,
      },
    }));
    if (annualLabourPolicyKeys.includes(policy)) {
      if (hasUploadedDocument(file)) {
        setErrors((prev) => {
          const next = { ...prev };
          const cur =
            typeof next[policy] === "object" && next[policy]
              ? { ...next[policy] }
              : {};
          delete cur.documentUrl;
          if (Object.keys(cur).length) next[policy] = cur;
          else delete next[policy];
          return next;
        });
      }
    } else {
      setErrors((prev) => ({
        ...prev,
        [policy]: {
          ...prev[policy],
          documentUrl: "",
        },
      }));
    }
  };
  const fetchData = useCallback(
    async (id) => {
      try {
        const response = await get(`/social/getSocial?id=${id}`);
        // Handle array response - get first item if array, otherwise use directly
        const responseData = response?.data?.data;
        const socialData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;
        const fetchedData = socialData?.labourPractices || {};
        const yearToSubmit = socialData?._socialDataRef?.yearToSubmit;
        const monthToSubmit = socialData?._socialDataRef?.monthToSubmit;
        const sanitizeDocumentUrl = (value) => {
          if (typeof value !== "string") return value || "";
          const cleaned = value.trim();
          if (!cleaned) return "";
          if (cleaned.toLowerCase() === "no documents available") return "";
          return cleaned;
        };
        let previousPoliciesMap = {};
        try {
          const currentEntryYear =
            Number(socialData?._socialDataRef?.yearToSubmit) ||
            Number(socialData?.yearToSubmit) ||
            Number(socialData?.year) ||
            undefined;
        setEntryYear(currentEntryYear || null);
          const companyId = socialData?.company || user?._id;
          const qs = new URLSearchParams();
          if (currentEntryYear) qs.set("year", String(currentEntryYear));
          if (companyId) qs.set("companyId", String(companyId));
          const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
          const prevRes = await get(prevUrl);
          setIsFirstEntry(Boolean(prevRes?.data?.data?.isFirstEntry));
          previousPoliciesMap = prevRes?.data?.data?.policies || {};
          setPreviousYearPolicyDocuments({
            labourLawCompliance: sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.labourLawCompliance"]
                ?.documentUrl || "",
            ),
            noChildLabourPolicy: sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.noChildLabourPolicy"]
                ?.documentUrl || "",
            ),
            noForcedLabourPolicy: sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.noForcedLabourPolicy"]
                ?.documentUrl || "",
            ),
            collectiveBargainingAgreements: sanitizeDocumentUrl(
              previousPoliciesMap?.[
                "labourPractices.collectiveBargainingAgreements"
              ]?.documentUrl || "",
            ),
            freedomOfAssociation: sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.freedomOfAssociation"]
                ?.documentUrl || "",
            ),
            legalMinimumWageCompliance: sanitizeDocumentUrl(
              previousPoliciesMap?.[
                "labourPractices.legalMinimumWageCompliance"
              ]?.documentUrl || "",
            ),
          });
        } catch (e) {
          previousPoliciesMap = {};
          setIsFirstEntry(false);
          setPreviousYearPolicyDocuments({
            labourLawCompliance: "",
            noChildLabourPolicy: "",
            noForcedLabourPolicy: "",
            collectiveBargainingAgreements: "",
            freedomOfAssociation: "",
            legalMinimumWageCompliance: "",
          });
        }

        // Get module from URL params, API response, or default to monthly
        const urlModule = searchParams.get("entryType");
        const apiModule =
          socialData?.module || socialData?._socialDataRef?.module;
        let finalModule = "monthly";

        if (
          urlModule === "annual" ||
          apiModule === "Annual" ||
          apiModule === "annual"
        ) {
          finalModule = "annual";
        } else if (
          urlModule === "monthly" ||
          apiModule === "Monthly" ||
          apiModule === "monthly"
        ) {
          finalModule = "monthly";
        }

        setModule(finalModule);

        // Capture meta for dashboard pending API
        const companyId = user?._id || socialData?.company || null;
        setEntryMeta({
          companyId,
          year: yearToSubmit ?? null,
          month: monthToSubmit ?? null,
        });

        // Separate annual and monthly data
        const annualData = {};
        const monthlyData = {};

        if (fetchedData) {
          // Annual fields
          const annualFields = [
            "labourLawCompliance",
            "noChildLabourPolicy",
            "noForcedLabourPolicy",
            "collectiveBargainingAgreements",
            "freedomOfAssociation",
            "assessments",
            "legalMinimumWageCompliance",
          ];

          // Monthly fields
          const monthlyFields = [
            "labourComplaints",
            "labourComplaintsResolved",
            "labourComplaintsPending",
            "labourComplaintsResolutionRate",
            "POSH",
          ];

          Object.keys(fetchedData).forEach((key) => {
            if (annualFields.includes(key)) {
              annualData[key] = fetchedData[key];
            } else if (monthlyFields.includes(key)) {
              monthlyData[key] =
                key === "POSH"
                  ? fetchedData[key]
                  : formatWithCommas(fetchedData[key]);
            }
          });
        }

        // Update annual form data
        // Transform assessments from API format to UI display format
        const predefinedTypeMap = {
          "(a)": { type: "(a) Child Labour", assessmentType: "Child Labour" },
          "(b)": { type: "(b) Forced Labour", assessmentType: "Forced Labour" },
          "(c)": {
            type: "(c) Sexual Harassment",
            assessmentType: "Sexual Harassment",
          },
          "(d)": {
            type: "(d) Discrimination at workplace",
            assessmentType: "Discrimination at workplace",
          },
          "(e)": { type: "(e) Wages", assessmentType: "Wages" },
        };

        // Initialize with default predefined assessments
        const defaultAssessments = [
          {
            type: "(a) Child Labour",
            facilitiesAssessed: "",
            percentageAssessed: "",
          },
          {
            type: "(b) Forced Labour",
            facilitiesAssessed: "",
            percentageAssessed: "",
          },
          {
            type: "(c) Sexual Harassment",
            facilitiesAssessed: "",
            percentageAssessed: "",
          },
          {
            type: "(d) Discrimination at workplace",
            facilitiesAssessed: "",
            percentageAssessed: "",
          },
          { type: "(e) Wages", facilitiesAssessed: "", percentageAssessed: "" },
          {
            type: "(f) Others",
            facilitiesAssessed: "",
            percentageAssessed: "",
          },
        ];

        if (
          annualData.assessments &&
          Array.isArray(annualData.assessments) &&
          annualData.assessments.length > 0
        ) {
          // Separate predefined, custom, and others from API data
          const customRows = [];
          let othersRow = null;

          // Match predefined assessments and populate data
          const matchedPredefined = defaultAssessments





            .slice(0, 5)
            .map((defaultAssessment) => {
              // Find matching assessment from API by type and assessmentType
              const apiAssessment = annualData.assessments.find((apiItem) => {
                // Check if it matches predefined format: {type: "(a)", assessmentType: "Child Labour"}
                const predefinedKey = Object.keys(predefinedTypeMap).find(
                  (key) =>
                    apiItem.type === key &&
                    apiItem.assessmentType ===
                      predefinedTypeMap[key].assessmentType,
                );
                return (
                  predefinedKey &&
                  predefinedTypeMap[predefinedKey].type ===
                    defaultAssessment.type
                );
              });

              // If found, use the data from API, otherwise use default
              return apiAssessment
                ? {
                    type: defaultAssessment.type, // Use UI format: "(a) Child Labour"
                    facilitiesAssessed: apiAssessment.facilitiesAssessed || "",
                    percentageAssessed: apiAssessment.percentageAssessed || "",
                  }
                : defaultAssessment;
            });

          // Get custom assessments (not predefined, not "Others")
          annualData.assessments.forEach((assessment) => {
            if (assessment.type && assessment.type.includes("Others")) {
              othersRow = assessment;
            } else {
              // Check if it's not a predefined type
              const isPredefined = Object.keys(predefinedTypeMap).some(
                (key) =>
                  assessment.type === key &&
                  assessment.assessmentType ===
                    predefinedTypeMap[key].assessmentType,
              );
              if (!isPredefined) {
                // Custom assessment: keep as is with assessmentType
                customRows.push({
                  type: assessment.type || "",
                  assessmentType: assessment.assessmentType || "",
                  facilitiesAssessed: assessment.facilitiesAssessed || "",
                  percentageAssessed: assessment.percentageAssessed || "",
                });
              }
            }
          });

          // Recalculate "Others" serial number
          const othersLetter = String.fromCharCode(102 + customRows.length); // 102 is 'f'
          const othersLabel = `(${othersLetter}) Others`;

          // Combine: predefined rows, custom rows, then "Others"
          annualData.assessments = [
            ...matchedPredefined,
            ...customRows,
            othersRow
              ? { ...othersRow, type: othersLabel }
              : {
                  type: othersLabel,
                  facilitiesAssessed: "",
                  percentageAssessed: "",
                },
          ];
        } else {
          // No assessments from API, use defaults
          annualData.assessments = defaultAssessments;
        }

        // Ensure policy radios default to "Yes" when status is missing from API payload
        [
          "labourLawCompliance",
          "noChildLabourPolicy",
          "noForcedLabourPolicy",
          "collectiveBargainingAgreements",
          "freedomOfAssociation",
          "legalMinimumWageCompliance",
        ].forEach((key) => {
          const prevPolicy =
            previousPoliciesMap?.[`labourPractices.${key}`] || {};
          const prevDocUrl = sanitizeDocumentUrl(prevPolicy?.documentUrl || "");
          const currentPolicy =
            annualData[key] && typeof annualData[key] === "object"
              ? annualData[key]
              : {};
          annualData[key] = {
            ...currentPolicy,
            status:
              currentPolicy.status === undefined
                ? parseStatusValue( true)
                : parseStatusValue(currentPolicy.status, true),
            isRevised:
              currentPolicy.isRevised === undefined
                ? parseStatusValue(prevPolicy?.isRevised, false)
                : parseStatusValue(currentPolicy.isRevised, false),
            documentUrl:
              sanitizeDocumentUrl(currentPolicy.documentUrl || "") || prevDocUrl,
            remark:
              typeof currentPolicy.remark === "string" &&
              currentPolicy.remark.trim() !== ""
                ? currentPolicy.remark
                : typeof prevPolicy?.remark === "string"
                  ? prevPolicy.remark
                  : "",
            effectiveDate:
              currentPolicy.effectiveDate || prevPolicy?.effectiveDate || "",
            statusremark:
              typeof currentPolicy.statusremark === "string" &&
              currentPolicy.statusremark.trim() !== ""
                ? currentPolicy.statusremark
                : "",
          };
        });

        setPreviousPolicyDocuments({
          labourLawCompliance:
            sanitizeDocumentUrl(annualData?.labourLawCompliance?.documentUrl || "") ||
            sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.labourLawCompliance"]
                ?.documentUrl || "",
            ),
          noChildLabourPolicy:
            sanitizeDocumentUrl(annualData?.noChildLabourPolicy?.documentUrl || "") ||
            sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.noChildLabourPolicy"]
                ?.documentUrl || "",
            ),
          noForcedLabourPolicy:
            sanitizeDocumentUrl(annualData?.noForcedLabourPolicy?.documentUrl || "") ||
            sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.noForcedLabourPolicy"]
                ?.documentUrl || "",
            ),
          collectiveBargainingAgreements:
            sanitizeDocumentUrl(
              annualData?.collectiveBargainingAgreements?.documentUrl || "",
            ) ||
            sanitizeDocumentUrl(
              previousPoliciesMap?.[
                "labourPractices.collectiveBargainingAgreements"
              ]?.documentUrl || "",
            ),
          freedomOfAssociation:
            sanitizeDocumentUrl(annualData?.freedomOfAssociation?.documentUrl || "") ||
            sanitizeDocumentUrl(
              previousPoliciesMap?.["labourPractices.freedomOfAssociation"]
                ?.documentUrl || "",
            ),
          legalMinimumWageCompliance:
            sanitizeDocumentUrl(
              annualData?.legalMinimumWageCompliance?.documentUrl || "",
            ) ||
            sanitizeDocumentUrl(
              previousPoliciesMap?.[
                "labourPractices.legalMinimumWageCompliance"
              ]?.documentUrl || "",
            ),
        });
        setPreviousPolicyMeta({
          labourLawCompliance: {
            remark:
              annualData?.labourLawCompliance?.remark ||
              previousPoliciesMap?.["labourPractices.labourLawCompliance"]
                ?.remark ||
              "",
            effectiveDate:
              annualData?.labourLawCompliance?.effectiveDate ||
              previousPoliciesMap?.["labourPractices.labourLawCompliance"]
                ?.effectiveDate ||
              "",
          },
          noChildLabourPolicy: {
            remark:
              annualData?.noChildLabourPolicy?.remark ||
              previousPoliciesMap?.["labourPractices.noChildLabourPolicy"]
                ?.remark ||
              "",
            effectiveDate:
              annualData?.noChildLabourPolicy?.effectiveDate ||
              previousPoliciesMap?.["labourPractices.noChildLabourPolicy"]
                ?.effectiveDate ||
              "",
          },
          noForcedLabourPolicy: {
            remark:
              annualData?.noForcedLabourPolicy?.remark ||
              previousPoliciesMap?.["labourPractices.noForcedLabourPolicy"]
                ?.remark ||
              "",
            effectiveDate:
              annualData?.noForcedLabourPolicy?.effectiveDate ||
              previousPoliciesMap?.["labourPractices.noForcedLabourPolicy"]
                ?.effectiveDate ||
              "",
          },
          collectiveBargainingAgreements: {
            remark:
              annualData?.collectiveBargainingAgreements?.remark ||
              previousPoliciesMap?.[
                "labourPractices.collectiveBargainingAgreements"
              ]?.remark ||
              "",
            effectiveDate:
              annualData?.collectiveBargainingAgreements?.effectiveDate ||
              previousPoliciesMap?.[
                "labourPractices.collectiveBargainingAgreements"
              ]?.effectiveDate ||
              "",
          },
          freedomOfAssociation: {
            remark:
              annualData?.freedomOfAssociation?.remark ||
              previousPoliciesMap?.["labourPractices.freedomOfAssociation"]
                ?.remark ||
              "",
            effectiveDate:
              annualData?.freedomOfAssociation?.effectiveDate ||
              previousPoliciesMap?.["labourPractices.freedomOfAssociation"]
                ?.effectiveDate ||
              "",
          },
          legalMinimumWageCompliance: {
            remark:
              annualData?.legalMinimumWageCompliance?.remark ||
              previousPoliciesMap?.[
                "labourPractices.legalMinimumWageCompliance"
              ]?.remark ||
              "",
            effectiveDate:
              annualData?.legalMinimumWageCompliance?.effectiveDate ||
              previousPoliciesMap?.[
                "labourPractices.legalMinimumWageCompliance"
              ]?.effectiveDate ||
              "",
          },
        });

        if (monthlyData.POSH && typeof monthlyData.POSH === "object") {
          const poshData = monthlyData.POSH;
          monthlyData.POSH = {
            ...poshData,
            status: parseStatusValue(poshData.status, true),
            filed: formatWithCommas(
              poshData.filed ??
                poshData.filedCases ??
                poshData.poshFiled ??
                "",
            ),
            resolved: formatWithCommas(
              poshData.resolved ??
                poshData.resolvedCases ??
                poshData.poshResolved ??
                "",
            ),
            pending: formatWithCommas(
              poshData.pending ??
                poshData.pendingCases ??
                poshData.poshPending ??
                "",
            ),
          };
        }

        setAnnualFormData((prevState) => ({
          ...prevState,
          ...annualData,
        }));

        // Normalize monthly pending value (it may come as object, number, or string)
        if (
          Object.prototype.hasOwnProperty.call(
            monthlyData,
            "labourComplaintsPending",
          )
        ) {
          const v = monthlyData.labourComplaintsPending;
          if (v && typeof v === "object") {
            monthlyData.labourComplaintsPending = String(
              v.pending ?? v.value ?? v.amount ?? "",
            );
          } else {
            monthlyData.labourComplaintsPending =
              v === undefined || v === null ? "" : String(v);
          }
        }

        // Update monthly form data
        setMonthlyFormData((prevState) => ({
          ...prevState,
          ...monthlyData,
          POSH: {
            ...prevState.POSH,
            ...(monthlyData.POSH || {}),
          },
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    [searchParams, user?._id],
  );

  // Fetch monthly pending complaints/totals from dashboard API whenever meta changes
  useEffect(() => {
    if (module !== "monthly") return;

    const { companyId, year, month } = entryMeta || {};
    if (!companyId || !year || month === undefined || month === null) return;

    const fetchPending = async () => {
      try {
        console.log("first12", { companyId, year, month });

        const pendingRes = await get(
          `/social/dashboard/monthlyLaborPracticesPending?companyId=${companyId}&year=${year}&month=${month}`,
        );
        console.log(pendingRes, "pendingRes");

        const payload =
          pendingRes?.data?.data ?? pendingRes?.data ?? pendingRes;

        // Extract historical totals { totalComplaints, totalResolved, totalPending }
        const totals =
          payload?.totals ?? pendingRes?.data?.totals ?? pendingRes?.totals;
        console.log("first123", { params: { companyId, year, month }, totals });
        if (totals) {
          pendingTotalsRef.current = {
            totalComplaints: Number(totals.totalComplaints) || 0,
            totalResolved: Number(totals.totalResolved) || 0,
            totalPending: Number(totals.totalPending) || 0,
          };
        }

        // IMPORTANT: Do NOT override labourComplaintsPending coming from /social/getSocial`.
        // We only use dashboard API pending totals for validation logic.
      } catch (e) {
        console.error("Failed to fetch monthlyLaborPracticesPending", e);
      }
    };

    fetchPending();
  }, [module, entryMeta, get]);

  // Fetch facilities
  const fetchFacilities = useCallback(async () => {
    try {
      const response = await get("/master/getAllFacility");
      setFacilities(response?.data?.data || []);
      console.log(response?.data?.data?.length, "facilities");
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setFacilities([]);
    }
  }, []);

  // Recalculate percentages when facilities data changes
  useEffect(() => {
    if (
      facilities.length > 0 &&
      annualFormData.assessments &&
      Array.isArray(annualFormData.assessments)
    ) {
      setAnnualFormData((prev) => {
        const updatedAssessments = prev.assessments.map((assessment) => {
          const facilitiesCount =
            assessment.facilitiesAssessed === ""
              ? 0
              : Number(assessment.facilitiesAssessed);
          const calculatedPercentage =
            facilities.length > 0
              ? ((facilitiesCount / facilities.length) * 100).toFixed(2)
              : "0.00";

          return {
            ...assessment,
            percentageAssessed: calculatedPercentage,
          };
        });

        return {
          ...prev,
          assessments: updatedAssessments,
        };
      });
    }
  }, [facilities.length]);

  useEffect(() => {
    fetchData(id);
    fetchFacilities();
  }, [id, fetchData, fetchFacilities]);

  const handlePolicyChange = (e, policy) => {
    const { value, name } = e.target;
    console.log(name, "for handling");
    const isStatusChange = name === "status";
    const isRevisedChange = name === "isRevised";
    const revisionPolicies = [
      "labourLawCompliance",
      "noChildLabourPolicy",
      "noForcedLabourPolicy",
      "collectiveBargainingAgreements",
      "freedomOfAssociation",
      "legalMinimumWageCompliance",
    ];
    const isRevisionPolicy = revisionPolicies.includes(policy);
    const revisedValue = value === "true";

    if (isRevisionPolicy && isRevisedChange && !revisedValue) {
      setDeletedPolicyDocs((prev) => ({ ...prev, [policy]: false }));
    }

    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        [name]:
          name === "remark" || name === "effectiveDate" || name === "statusremark"
            ? value
            : value === "true"
              ? true
              : false,
        ...(isRevisionPolicy && isRevisedChange && !revisedValue
          ? {
              documentUrl:
                previousPolicyDocuments[policy] ||
                prev[policy]?.documentUrl ||
                "",
              remark:
                previousPolicyMeta?.[policy]?.remark ||
                prev[policy]?.remark ||
                "",
              effectiveDate:
                previousPolicyMeta?.[policy]?.effectiveDate ||
                prev[policy]?.effectiveDate ||
                "",
            }
          : {}),
        ...(isRevisionPolicy && isRevisedChange && revisedValue
          ? { documentUrl: "", remark: "", effectiveDate: "" }
          : {}),
        ...(isRevisionPolicy && isStatusChange && value === "false"
          ? { isRevised: false, documentUrl: "", effectiveDate: "", remark: "" }
          : {}),
      },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      const cur =
        typeof next[policy] === "object" && next[policy]
          ? { ...next[policy] }
          : {};

      // If status is "No", clear this section's errors entirely.
      if (name === "status" && value === "false") {
        delete next[policy];
        return next;
      }

      // Clear only the corresponding field error on change.
      delete cur[name];
      if (name === "isRevised") {
        delete cur.documentUrl;
        delete cur.remark;
        delete cur.effectiveDate;
      }

      if (Object.keys(cur).length) next[policy] = cur;
      else delete next[policy];
      return next;
    });
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);

    if (!/^\d*$/.test(normalizedValue)) return;

    // Monthly: auto-calc pending = labourComplaints - labourComplaintsResolved
    // and validate against historical totals from dashboard API
    if (
      module === "monthly" &&
      (name === "labourComplaints" || name === "labourComplaintsResolved")
    ) {
      const newTotal =
        name === "labourComplaints"
          ? Number(normalizedValue || 0)
          : Number(removeCommas(formData.labourComplaints || 0));
      const newResolved =
        name === "labourComplaintsResolved"
          ? Number(normalizedValue || 0)
          : Number(removeCommas(formData.labourComplaintsResolved || 0));

      // Historical totals from API
      const { totalPending } = pendingTotalsRef.current || {};
      const totalPendingNum = Number(totalPending) || 0;

      // Pending:
      // - If totalPending == 0:
      //     pending = max(labourComplaints - labourComplaintsResolved, 0)
      // - If totalPending > 0:
      //     pending = (labourComplaints + totalPendingNum) - labourComplaintsResolved
      //       (and not less than 0)
      const pending =
        totalPendingNum > 0
          ? Math.max(newTotal + totalPendingNum - newResolved, 0)
          : Math.max(newTotal - newResolved, 0);
      // Auto-calc resolution rate:
      // - when historical pending exists, denominator = current complaints + historical pending
      // - otherwise denominator = current complaints
      const totalForResolution =
        totalPendingNum > 0 ? newTotal + totalPendingNum : newTotal;
      const resolutionRate =
        totalForResolution > 0
          ? ((newResolved / totalForResolution) * 100).toFixed(2)
          : "0.00";

      let resolvedError = "";
      if (totalPendingNum === 0) {
        // Case 1: No historical pending -> resolved cannot exceed current complaints
        if (newResolved > newTotal) {
          resolvedError = "Resolved complaints cannot exceed total complaints.";
        }
      } else if (totalPendingNum > 0) {
        // Case 2: There is historical pending -> resolved cannot exceed current complaints + historical pending
        if (newResolved > newTotal + totalPendingNum) {
          resolvedError =
            "Resolved complaints cannot exceed total of current complaints plus pending from previous periods.";
        }
      }
      setFormData((prev) => ({
        ...prev,
        [name]: formatWithCommas(normalizedValue),
        labourComplaintsPending:
          pending === 0 ? "0" : formatWithCommas(String(pending)),
        labourComplaintsResolutionRate: formatWithCommas(resolutionRate),
      }));

      setErrors((prev) => ({
        ...prev,
        [name]: "",
        labourComplaintsResolved: resolvedError,
        ...(name === "labourComplaints"
          ? {
              labourComplaintsPending: "",
              labourComplaintsResolutionRate: "",
            }
          : {}),
      }));

      return;
    }

    console.log("Number", name, value);
    setFormData((prev) => ({
      ...prev,
      [name]: formatWithCommas(normalizedValue),
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // We still use API pending only for validation; the displayed pending value
  // is always kept in sync with the current total / resolved inside handleNumberChange.
  const handleNumberChangePOSH = (e, POSH) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);
    if (!/^\d*$/.test(normalizedValue)) return;

    setFormData((prev) => ({
      ...prev,
      [POSH]: {
        ...prev[POSH],
          [name]: formatWithCommas(normalizedValue),
      },
    }));
    setErrors((prev) => ({
      ...prev,
      [POSH]: {
        ...prev[POSH],
        [name]: "",
      },
    }));
  };

  const handleDigitOnlyKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];
    if (allowedKeys.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleDigitOnlyPaste = (e) => {
    const pasteText = e.clipboardData.getData("text");
    if (!/^\d*$/.test(pasteText)) {
      e.preventDefault();
    }
  };

  const handleAssessmentChange = (index, field, value) => {
    if (field === "facilitiesAssessed" || field === "percentageAssessed") {
      if (field === "percentageAssessed") {
        // Percentage is auto-calculated, so we don't allow manual input
        return;
      } else {
        const normalizedValue = removeCommas(value);
        if (!/^\d*$/.test(normalizedValue)) return;
        value = normalizedValue;
      }
    }

    setFormData((prev) => {
      const updatedAssessments = [...prev.assessments];
      const totalFacilities = facilities.length || 0; // Avoid division by zero

      if (field === "facilitiesAssessed") {
        // Auto-calculate percentage: (facilitiesAssessed / totalFacilities) * 100
        const facilitiesCount = value === "" ? 0 : Number(value);
        const calculatedPercentage =
          totalFacilities > 0
            ? ((facilitiesCount / totalFacilities) * 100).toFixed(2)
            : "0.00";

        updatedAssessments[index] = {
          ...updatedAssessments[index],
          facilitiesAssessed: value,
          percentageAssessed: formatWithCommas(calculatedPercentage),
        };
      } else {
        updatedAssessments[index] = {
          ...updatedAssessments[index],
          [field]: value,
        };
      }

      return {
        ...prev,
        assessments: updatedAssessments,
      };
    });

    setErrors((prev) => {
      const updatedErrors = { ...prev };
      if (updatedErrors.assessments && updatedErrors.assessments[index]) {
        updatedErrors.assessments[index] = {
          ...updatedErrors.assessments[index],
          [field]: "",
        };
      }
      return updatedErrors;
    });
  };

  const handleAssessmentTypeChange = (index, value) => {
    setFormData((prev) => {
      const updatedAssessments = [...prev.assessments];
      updatedAssessments[index] = {
        ...updatedAssessments[index],
        assessmentType: value,
      };
      return {
        ...prev,
        assessments: updatedAssessments,
      };
    });

    setErrors((prev) => {
      const updatedErrors = { ...prev };
      if (updatedErrors.assessments && updatedErrors.assessments[index]) {
        updatedErrors.assessments[index] = {
          ...updatedErrors.assessments[index],
          assessmentType: "",
        };
      }
      return updatedErrors;
    });
  };

  const handleAddAssessment = () => {
    setFormData((prev) => {
      const assessments = prev.assessments || [];
      const appendedAssessments = [
        ...assessments,
        {
          type: "",
          assessmentType: "",
          facilitiesAssessed: "",
          percentageAssessed: "",
        },
      ];

      return {
        ...prev,
        assessments: rebuildAssessments(appendedAssessments),
      };
    });
  };

  const canAddAssessment = () => {
    const assessments = formData.assessments || [];
    const nonOthersRows = assessments.filter(
      (row) => !(row?.type && row.type.includes("Others")),
    );

    const customRows = nonOthersRows.filter(
      (row) => !predefinedAssessmentTypes.includes(row?.type || ""),
    );

    // Allow adding the first custom row. After that, require each custom row to be complete.
    if (customRows.length === 0) return true;

    return customRows.every(
      (row) =>
        String(row?.assessmentType || "").trim() !== "" &&
        String(row?.facilitiesAssessed || "").trim() !== "",
    );
  };

  const handleDeleteAssessment = (indexToDelete) => {
    setFormData((prev) => {
      const filteredAssessments = (prev.assessments || []).filter(
        (_, index) => index !== indexToDelete,
      );
      return {
        ...prev,
        assessments: rebuildAssessments(filteredAssessments),
      };
    });

    setErrors((prev) => {
      if (!prev?.assessments || !Array.isArray(prev.assessments)) return prev;
      return {
        ...prev,
        assessments: prev.assessments.filter(
          (_, index) => index !== indexToDelete,
        ),
      };
    });
  };

  const confirmDeleteAssessment = () => {
    if (deleteAssessmentIndex !== null && deleteAssessmentIndex !== undefined) {
      handleDeleteAssessment(deleteAssessmentIndex);
    }
    setDeleteDialog(false);
    setDeleteAssessmentIndex(null);
  };

  const handleSubmit = async () => {
    if (validation()) return;

    // Send the appropriate formData based on module
    const dataToSubmit = module === "annual" ? annualFormData : monthlyFormData;

    // Clean up the payload: if status is false, set documentUrl to ""
    let cleanedFormData = {};
    if (module === "annual") {
      // Filter out "Others" row and transform assessments to consistent format
      const predefinedTypes = [
        "(a) Child Labour",
        "(b) Forced Labour",
        "(c) Sexual Harassment",
        "(d) Discrimination at workplace",
        "(e) Wages",
      ];

      const transformedAssessments = (dataToSubmit.assessments || [])
        .filter(
          (assessment) =>
            !assessment.type || !assessment.type.includes("Others"),
        )
        .map((assessment) => {
          const isPredefined = predefinedTypes.includes(assessment.type);

          if (isPredefined) {
            // Built-in assessment: transform "(a) Child Labour" to {type: "(a)", assessmentType: "Child Labour"}
            const typeMatch = assessment.type?.match(/^\(([a-z])\)\s*(.+)$/i);
            if (typeMatch) {
              return {
                type: `(${typeMatch[1]})`,
                assessmentType: typeMatch[2],
                facilitiesAssessed: assessment.facilitiesAssessed || "",
                percentageAssessed: assessment.percentageAssessed || "",
              };
            }
          }

          // Custom assessment: format as {type: "(g)", assessmentType: "quick", ...}
          return {
            type: assessment.type || "",
            assessmentType: assessment.assessmentType || "",
            facilitiesAssessed: assessment.facilitiesAssessed || "",
            percentageAssessed: assessment.percentageAssessed || "",
          };
        });

      cleanedFormData = {
        ...dataToSubmit,
        assessments: transformedAssessments,
        labourLawCompliance: {
          ...dataToSubmit.labourLawCompliance,
          ...(dataToSubmit.labourLawCompliance.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
        noChildLabourPolicy: {
          ...dataToSubmit.noChildLabourPolicy,
          ...(dataToSubmit.noChildLabourPolicy.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
        noForcedLabourPolicy: {
          ...dataToSubmit.noForcedLabourPolicy,
          ...(dataToSubmit.noForcedLabourPolicy.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
        collectiveBargainingAgreements: {
          ...dataToSubmit.collectiveBargainingAgreements,
          ...(dataToSubmit.collectiveBargainingAgreements.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
        freedomOfAssociation: {
          ...dataToSubmit.freedomOfAssociation,
          ...(dataToSubmit.freedomOfAssociation.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
        legalMinimumWageCompliance: {
          ...dataToSubmit.legalMinimumWageCompliance,
          ...(dataToSubmit.legalMinimumWageCompliance.status === false
            ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
            : {}),
        },
      };
    } else {
      cleanedFormData = {
        ...dataToSubmit,
        POSH: {
          ...dataToSubmit.POSH,
          ...(dataToSubmit.POSH.status === false
            ? { documentUrl: "", filed: "", resolved: "", pending: "" }
            : {}),
        },
      };
    }

    const payload = {
      labourPractices: stripCommasFromNumericStrings(cleanedFormData),
      stepsCompleted: {
        labourPractices: true,
      },
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((res) => {
        const entryTypeParam = module === "annual" ? "annual" : "monthly";
        navigate(`/social/data/health-safety/${id}`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getDisplayedPolicyDocument = (policyKey) =>
    deletedPolicyDocs?.[policyKey]
      ? formData?.[policyKey]?.documentUrl || ""
      : formData?.[policyKey]?.documentUrl ||
        (formData?.[policyKey]?.isRevised
          ? ""
          : previousPolicyDocuments?.[policyKey] || "");

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <DeleteModal
        onOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setDeleteAssessmentIndex(null);
        }}
        onConfirm={confirmDeleteAssessment}
      />
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <p className={styles.heading + " " + styles.form_headingStrongUpper}>
          D. Labour Practices
        </p>

        {/* Conditionally render based on module type */}
        {module === "annual" ? (
          // Annual Module - Questions 1-5, Assessments table, Question 7
          <>
            {/* Labour Law Compliance */}
            <BooleanRadioGroup
              label="1. Compliance with Labour Laws:"
              name="status"
              value={formData.labourLawCompliance.status}
              onChange={(e) => handlePolicyChange(e, "labourLawCompliance")}
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {formData.labourLawCompliance.status ? (
                <div
                  style={{
                    marginTop: "-1rem",
                   
                  }}
                >
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearPolicyDocuments.labourLawCompliance,
                  ) && (
                    <BooleanRadioGroup
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={
                        formData.labourLawCompliance.isRevised
                          ? "true"
                          : "false"
                      }
                      onChange={(e) =>
                        handlePolicyChange(e, "labourLawCompliance")
                      }
                    />
                  )}
                  <Box sx={{ ml: "0rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(
                            getDisplayedPolicyDocument("labourLawCompliance"),
                          )
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "labourLawCompliance")
                          }
                          filesList={getDisplayedPolicyDocument("labourLawCompliance")}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearPolicyDocuments.labourLawCompliance,
                          )}
                        />
                        {errors?.labourLawCompliance?.documentUrl ? (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "14px",
                              minHeight: "20px",
                            }}
                          >
                            {errors?.labourLawCompliance?.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(
                            getDisplayedPolicyDocument("labourLawCompliance"),
                          )
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(getDisplayedPolicyDocument("labourLawCompliance")) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.labourLawCompliance.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "labourLawCompliance")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={!!errors?.labourLawCompliance?.remark}
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.labourLawCompliance?.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={formData.labourLawCompliance.effectiveDate || ""}
                      inputProps={{
                        ...(labourPolicyEffMin && labourPolicyEffMax
                          ? { min: "2021-01-01", max: labourPolicyEffMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) =>
                        handlePolicyChange(e, "labourLawCompliance")
                      }
                      size="small"
                      error={!!errors?.labourLawCompliance?.effectiveDate}
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {errors?.labourLawCompliance?.effectiveDate}
                    </FormHelperText>
                  </Box>
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "1rem" }}>
                  {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.labourLawCompliance.statusremark || ""}
                    onChange={(e) => handlePolicyChange(e, "labourLawCompliance")}
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>

            {/* No Child Labour Policy */}
            <BooleanRadioGroup
              label="2. No Child Labour policy and practices:"
              name="status"
              value={formData.noChildLabourPolicy.status}
              onChange={(e) => handlePolicyChange(e, "noChildLabourPolicy")}
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {formData.noChildLabourPolicy.status ? (
                <div
                  style={{
                    marginTop: "-1rem",
                  }}
                >
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearPolicyDocuments.noChildLabourPolicy,
                  ) && (
                    <BooleanRadioGroup
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={
                        formData.noChildLabourPolicy.isRevised
                          ? "true"
                          : "false"
                      }
                      onChange={(e) =>
                        handlePolicyChange(e, "noChildLabourPolicy")
                      }
                    />
                  )}
                  <Box sx={{ ml: "0rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(
                            getDisplayedPolicyDocument("noChildLabourPolicy"),
                          )
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "noChildLabourPolicy")
                          }
                          filesList={getDisplayedPolicyDocument("noChildLabourPolicy")}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearPolicyDocuments.noChildLabourPolicy,
                          )}
                        />
                        {errors?.noChildLabourPolicy?.documentUrl ? (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "14px",
                              minHeight: "20px",
                            }}
                          >
                            {errors?.noChildLabourPolicy?.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(
                            getDisplayedPolicyDocument("noChildLabourPolicy"),
                          )
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(getDisplayedPolicyDocument("noChildLabourPolicy")) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.noChildLabourPolicy.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "noChildLabourPolicy")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={!!errors?.noChildLabourPolicy?.remark}
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.noChildLabourPolicy?.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={formData.noChildLabourPolicy.effectiveDate || ""}
                      inputProps={{
                        ...(labourPolicyEffMin && labourPolicyEffMax
                          ? { min: "2021-01-01", max: labourPolicyEffMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) => handlePolicyChange(e, "noChildLabourPolicy")}
                      size="small"
                      error={!!errors?.noChildLabourPolicy?.effectiveDate}
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {errors?.noChildLabourPolicy?.effectiveDate}
                    </FormHelperText>
                  </Box>
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "1rem" }}>
                  {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.noChildLabourPolicy.statusremark || ""}
                    onChange={(e) => handlePolicyChange(e, "noChildLabourPolicy")}
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>
            {/* No Forced Labour Policy */}
            <BooleanRadioGroup
              label="3. No Forced Labour policy and practices:"
              name="status"
              value={formData.noForcedLabourPolicy.status}
              onChange={(e) => handlePolicyChange(e, "noForcedLabourPolicy")}
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {formData.noForcedLabourPolicy.status ? (
                <div
                  style={{
                    marginTop: "-1rem",
                  }}
                >
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearPolicyDocuments.noForcedLabourPolicy,
                  ) && (
                    <BooleanRadioGroup
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={
                        formData.noForcedLabourPolicy.isRevised
                          ? "true"
                          : "false"
                      }
                      onChange={(e) =>
                        handlePolicyChange(e, "noForcedLabourPolicy")
                      }
                    />
                  )}
                  <Box sx={{ ml: "0rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(
                            getDisplayedPolicyDocument("noForcedLabourPolicy"),
                          )
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "noForcedLabourPolicy")
                          }
                          filesList={getDisplayedPolicyDocument("noForcedLabourPolicy")}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearPolicyDocuments.noForcedLabourPolicy,
                          )}
                        />
                        {errors?.noForcedLabourPolicy?.documentUrl ? (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "14px",
                              minHeight: "20px",
                            }}
                          >
                            {errors?.noForcedLabourPolicy?.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(
                            getDisplayedPolicyDocument("noForcedLabourPolicy"),
                          )
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(getDisplayedPolicyDocument("noForcedLabourPolicy")) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.noForcedLabourPolicy.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "noForcedLabourPolicy")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={!!errors?.noForcedLabourPolicy?.remark}
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.noForcedLabourPolicy?.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={formData.noForcedLabourPolicy.effectiveDate || ""}
                      inputProps={{
                        ...(labourPolicyEffMin && labourPolicyEffMax
                          ? { min: "2021-01-01", max: labourPolicyEffMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) => handlePolicyChange(e, "noForcedLabourPolicy")}
                      size="small"
                      error={!!errors?.noForcedLabourPolicy?.effectiveDate}
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {errors?.noForcedLabourPolicy?.effectiveDate}
                    </FormHelperText>
                  </Box>
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "1rem" }}>
                  {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.noForcedLabourPolicy.statusremark || ""}
                    onChange={(e) => handlePolicyChange(e, "noForcedLabourPolicy")}
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>
            {/* Collective Bargaining Agreements */}
            <BooleanRadioGroup
              label="4. Presence of Right to  Collective Bargaining Agreements (CBA):"
              name="status"
              value={
                formData.collectiveBargainingAgreements.status
                  ? "true"
                  : "false"
              }
              onChange={(e) =>
                handlePolicyChange(e, "collectiveBargainingAgreements")
              }
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {formData.collectiveBargainingAgreements.status ? (
                <div
                  style={{
                    marginTop: "-1rem",
                  }}
                >
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearPolicyDocuments.collectiveBargainingAgreements,
                  ) && (
                    <BooleanRadioGroup
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={
                        formData.collectiveBargainingAgreements.isRevised
                          ? "true"
                          : "false"
                      }
                      onChange={(e) =>
                        handlePolicyChange(e, "collectiveBargainingAgreements")
                      }
                    />
                  )}
                  <Box sx={{ ml: "0rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(
                            getDisplayedPolicyDocument(
                              "collectiveBargainingAgreements",
                            ),
                          )
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "collectiveBargainingAgreements")
                          }
                          filesList={getDisplayedPolicyDocument(
                            "collectiveBargainingAgreements",
                          )}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearPolicyDocuments.collectiveBargainingAgreements,
                          )}
                        />
                        {errors?.collectiveBargainingAgreements?.documentUrl ? (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "14px",
                              minHeight: "20px",
                            }}
                          >
                            {errors?.collectiveBargainingAgreements?.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(
                            getDisplayedPolicyDocument(
                              "collectiveBargainingAgreements",
                            ),
                          )
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(getDisplayedPolicyDocument("collectiveBargainingAgreements")) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.collectiveBargainingAgreements.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "collectiveBargainingAgreements")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={!!errors?.collectiveBargainingAgreements?.remark}
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.collectiveBargainingAgreements?.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={
                        formData.collectiveBargainingAgreements.effectiveDate || ""
                      }
                      inputProps={{
                        ...(labourPolicyEffMin && labourPolicyEffMax
                          ? { min: "2021-01-01", max: labourPolicyEffMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) =>
                        handlePolicyChange(e, "collectiveBargainingAgreements")
                      }
                      size="small"
                      error={!!errors?.collectiveBargainingAgreements?.effectiveDate}
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {errors?.collectiveBargainingAgreements?.effectiveDate}
                    </FormHelperText>
                  </Box>
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "1rem" }}>
                  {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.collectiveBargainingAgreements.statusremark || ""}
                    onChange={(e) =>
                      handlePolicyChange(e, "collectiveBargainingAgreements")
                    }
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>
            {/* Freedom of Association */}
            <BooleanRadioGroup
              label="5. Presence of Right to Freedom of Association:"
              name="status"
              value={formData.freedomOfAssociation.status}
              onChange={(e) => handlePolicyChange(e, "freedomOfAssociation")}
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {formData.freedomOfAssociation.status ? (
                <div
                  style={{
                    marginTop: "-1rem",
                  }}
                >
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearPolicyDocuments.freedomOfAssociation,
                  ) && (
                    <BooleanRadioGroup
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={
                        formData.freedomOfAssociation.isRevised
                          ? "true"
                          : "false"
                      }
                      onChange={(e) =>
                        handlePolicyChange(e, "freedomOfAssociation")
                      }
                    />
                  )}
                  <Box sx={{ ml: "0rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(
                            getDisplayedPolicyDocument("freedomOfAssociation"),
                          )
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "freedomOfAssociation")
                          }
                          filesList={getDisplayedPolicyDocument("freedomOfAssociation")}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearPolicyDocuments.freedomOfAssociation,
                          )}
                        />
                        {errors?.freedomOfAssociation?.documentUrl ? (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "14px",
                              minHeight: "20px",
                            }}
                          >
                            {errors?.freedomOfAssociation?.documentUrl}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(
                            getDisplayedPolicyDocument("freedomOfAssociation"),
                          )
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(getDisplayedPolicyDocument("freedomOfAssociation")) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.freedomOfAssociation.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "freedomOfAssociation")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={!!errors?.freedomOfAssociation?.remark}
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.freedomOfAssociation?.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={formData.freedomOfAssociation.effectiveDate || ""}
                      inputProps={{
                        ...(labourPolicyEffMin && labourPolicyEffMax
                          ? { min: "2021-01-01", max: labourPolicyEffMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) =>
                        handlePolicyChange(e, "freedomOfAssociation")
                      }
                      size="small"
                      error={!!errors?.freedomOfAssociation?.effectiveDate}
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {errors?.freedomOfAssociation?.effectiveDate}
                    </FormHelperText>
                  </Box>
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "1rem" }}>
                  {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.freedomOfAssociation.statusremark || ""}
                    onChange={(e) => handlePolicyChange(e, "freedomOfAssociation")}
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>

            {/* Assessments Section */}
            <div style={{ marginBottom: "1rem", marginTop: "1rem" }}>
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                6. Assessments for the year:
              </p>

              {/* Column Headers */}
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  marginTop: "10px",
                  paddingLeft: "1rem",
                }}
              >
                <p
                  style={{
                    width: "30%",
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "0px",
                  }}
                >
                  Type of Assessments
                </p>
                <p
                  style={{
                    width: "30%",
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "0px",
                  }}
                >
                  Number of Facilities assessed
                </p>
                <p
                  style={{
                    width: "30%",
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "0px",
                  }}
                >
                  % of plants/offices that are assessed
                </p>
              </div>

              {/* Assessment Rows */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  marginTop: "0.5rem",
                }}
              >
                {(formData.assessments || []).map((assessment, index) => {
                  const assessmentType = assessment.type || "";
                  const isPredefined =
                    predefinedAssessmentTypes.includes(assessmentType);
                  const isOthersRow =
                    assessmentType && assessmentType.includes("Others");
                  const isCustomRow = !isPredefined && !isOthersRow; // Custom rows like (g), (h), etc.

                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "1rem",
                        paddingLeft: "1rem",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: "30%",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        {isCustomRow ? (
                          <>
                            <div
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                alignItems: "center",
                              }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "14px",
                                  minWidth: "0px",
                                }}
                              >
                                {assessmentType}
                              </p>
                              <TextField
                                value={assessment.assessmentType || ""}
                                onChange={(e) =>
                                  handleAssessmentTypeChange(
                                    index,
                                    e.target.value,
                                  )
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Enter Assessment Type"
                                error={
                                  !!errors?.assessments?.[index]?.[
                                    "assessmentType"
                                  ]
                                }
                                sx={{ flex: 1 }}
                              />
                            </div>
                            {errors?.assessments?.[index]?.[
                              "assessmentType"
                            ] && (
                              <FormHelperText
                                sx={{ color: "#D34040", fontSize: "12px",ml:isCustomRow?"1.5rem":"0rem" }}
                              >
                                {`${errors.assessments[index]["assessmentType"]}`}
                              </FormHelperText>
                            )}
                          </>
                        ) : (
                          <p style={{ margin: 0, fontSize: "14px" }}>
                            {assessmentType}
                          </p>
                        )}
                      </div>
                      {isOthersRow ? (
                        <div style={{ width: "30%" }}>
                          <Button
                            variant="outlined"
                            onClick={handleAddAssessment}
                            disabled={!canAddAssessment()}
                            sx={{
                              textTransform: "none",
                              minWidth: "auto",
                              padding: "6px 12px",
                              color: "#1C486B",
                              borderColor: "#1C486B",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Add +
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              width: "30%",
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.5rem",
                            }}
                          >
                            <TextField
                              value={assessment.facilitiesAssessed || ""}
                              onChange={(e) =>
                                handleAssessmentChange(
                                  index,
                                  "facilitiesAssessed",
                                  e.target.value,
                                )
                              }
                              variant="outlined"
                              size="small"
                              placeholder="Enter Numbers"
                              error={
                                !!errors?.assessments?.[index]?.[
                                  "facilitiesAssessed"
                                ]
                              }
                              sx={{ width: "100%" }}
                            />
                            {errors?.assessments?.[index]?.[
                              "facilitiesAssessed"
                            ] && (
                              <FormHelperText
                                sx={{ color: "#D34040", fontSize: "12px" }}
                              >
                                {
                                  errors.assessments[index][
                                    "facilitiesAssessed"
                                  ]
                                }
                              </FormHelperText>
                            )}
                          </div>
                          <div
                            style={{
                              width: "30%",
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.5rem",
                            }}
                          >
                            <TextField
                              value={assessment.percentageAssessed || ""}
                              onChange={(e) =>
                                handleAssessmentChange(
                                  index,
                                  "percentageAssessed",
                                  e.target.value,
                                )
                              }
                              variant="outlined"
                              size="small"
                              placeholder="Auto-calculated"
                              error={
                                !!errors?.assessments?.[index]?.[
                                  "percentageAssessed"
                                ]
                              }
                              disabled={true}
                              sx={{ width: "100%" }}
                            />
                            {errors?.assessments?.[index]?.[
                              "percentageAssessed"
                            ] && (
                              <FormHelperText
                                sx={{ color: "#D34040", fontSize: "12px" }}
                              >
                                {
                                  errors.assessments[index][
                                    "percentageAssessed"
                                  ]
                                }
                              </FormHelperText>
                            )}
                          </div>
                          {isCustomRow && (
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeleteAssessmentIndex(index);
                                  setDeleteDialog(true);
                                }}
                                aria-label="Delete assessment row"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legal Minimum Wage Compliance */}
            <BooleanRadioGroup
              label="7. Compliance with legal minimum wage:"
              name="status"
              value={formData.legalMinimumWageCompliance.status}
              onChange={(e) =>
                handlePolicyChange(e, "legalMinimumWageCompliance")
              }
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {formData.legalMinimumWageCompliance.status ? (
                <div
                  style={{
                    marginTop: "-1rem",
                  }}
                >
                  {shouldShowAnnualPolicyRevised(
                    !isFirstEntry,
                    previousYearPolicyDocuments.legalMinimumWageCompliance,
                  ) && (
                    <BooleanRadioGroup
                      label="Is the Policy revised?"
                      name="isRevised"
                      value={
                        formData.legalMinimumWageCompliance.isRevised
                          ? "true"
                          : "false"
                      }
                      onChange={(e) =>
                        handlePolicyChange(e, "legalMinimumWageCompliance")
                      }
                    />
                  )}
                  <Box sx={{ ml: "0rem" }}>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                    >
                      Upload evidence (for Policy):
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        ml: "1.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: hasUploadedDocument(
                            getDisplayedPolicyDocument("legalMinimumWageCompliance"),
                          )
                            ? "233px"
                            : "100px",
                        }}
                      >
                        <FileUpload
                          setDocumentUrl={(file) =>
                            handleFileChange(file, "legalMinimumWageCompliance")
                          }
                          filesList={getDisplayedPolicyDocument(
                            "legalMinimumWageCompliance",
                          )}
                          isDisabled={isAnnualPolicyUploadDisabled(
                            !isFirstEntry,
                            previousYearPolicyDocuments.legalMinimumWageCompliance,
                          )}
                        />
                       {errors?.legalMinimumWageCompliance?.documentUrl ?
                        <FormHelperText
                        sx={{
                          color: "#D34040",
                          fontSize: "14px",
                          minHeight: "20px",
                        }}
                      >
                        {errors?.legalMinimumWageCompliance?.documentUrl}
                      </FormHelperText>
                       : null}
                       
                      </Box>
                      <Box
                        sx={{
                          width: "30%",
                          mt: hasUploadedDocument(
                            getDisplayedPolicyDocument("legalMinimumWageCompliance"),
                          )
                            ? "15px"
                            : "10px",
                            ml:hasUploadedDocument(getDisplayedPolicyDocument("legalMinimumWageCompliance")) ? "0" : "1.1rem",
                        }}
                      >
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.legalMinimumWageCompliance.remark || ""}
                          onChange={(e) =>
                            handlePolicyChange(e, "legalMinimumWageCompliance")
                          }
                          placeholder="Enter Additional Text"
                          size="small"
                          multiline
                          minRows={1}
                          error={!!errors?.legalMinimumWageCompliance?.remark}
                          InputProps={{ sx: { padding: "9px 12px" } }}
                        />
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "14px",
                            minHeight: "20px",
                          }}
                        >
                          {errors?.legalMinimumWageCompliance?.remark || " "}
                        </FormHelperText>
                      </Box>
                    </Box>
                    <Typography
                      component="p"
                      className={styles.form_heading}
                      sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                    >
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      name="effectiveDate"
                      value={formData.legalMinimumWageCompliance.effectiveDate || ""}
                      inputProps={{
                        ...(labourPolicyEffMin && labourPolicyEffMax
                          ? { min: "2021-01-01", max: labourPolicyEffMax }
                          : { max: maxEffectiveDate }),
                      }}
                      onChange={(e) =>
                        handlePolicyChange(e, "legalMinimumWageCompliance")
                      }
                      size="small"
                      error={!!errors?.legalMinimumWageCompliance?.effectiveDate}
                      sx={{
                        width: "30%",
                        ml: "1.1rem",
                        mb: "0.25rem",
                        "& .MuiInputBase-root": { height: 40 },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        marginLeft: "1.1rem",
                        width: "30%",
                      }}
                    >
                      {errors?.legalMinimumWageCompliance?.effectiveDate}
                    </FormHelperText>
                  </Box>
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "1rem" }}>
                  {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
                    Remark
                  </p> */}
                  <TextField
                    name="statusremark"
                    value={formData.legalMinimumWageCompliance.statusremark || ""}
                    onChange={(e) =>
                      handlePolicyChange(e, "legalMinimumWageCompliance")
                    }
                    placeholder="Enter Additional Text"
                    size="small"
                    sx={{ width: "40%" }}
                  />
                </div>
              )}
            </Box>
          </>
        ) : (
          // Monthly Module - Questions 6, 7, 8 (POSH)
          <>
            {/* Labour Complaints */}
            <div style={{ marginBottom: "1rem" }}>
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                1. Number of Labour-Related Complaints:
              </p>
              <TextField
                name="labourComplaints"
                value={formData.labourComplaints}
                onChange={handleNumberChange}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ pl: 2, width: "50%" }}
                placeholder="Enter Number"
                error={errors?.["labourComplaints"]}
              />
              {errors?.["labourComplaints"] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.["labourComplaints"]}
                </FormHelperText>
              )}
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                2. Number of Labour-Related Resolved Complaints:
              </p>
              <TextField
                name="labourComplaintsResolved"
                value={formData.labourComplaintsResolved}
                onChange={handleNumberChange}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ pl: 2, width: "50%" }}
                placeholder="Enter Number"
                error={Boolean(errors?.["labourComplaintsResolved"])}
              />
              {errors?.["labourComplaintsResolved"] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.["labourComplaintsResolved"]}
                </FormHelperText>
              )}
            </div>

            {/* Labour Complaints Pending (auto-calculated) */}
            <div style={{ marginBottom: "1rem" }}>
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                {currentTotalPending === 0
                  ? "3. Pending Labour-Related Complaints:"
                  : "3.   Pending Labour-Related Complaints (Auto-calculated):"}
              </p>
              <TextField
                name="labourComplaintsPending"
                value={
                  module === "monthly"
                    ? (monthlyFormData.labourComplaintsPending ?? "")
                    : ""
                }
                variant="outlined"
                fullWidth
                size="small"
                sx={{ pl: 2, width: "50%" }}
                placeholder="Auto-calculated"
                disabled
                error={!!errors?.labourComplaintsPending}
              />
              {errors?.["labourComplaintsPending"] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.["labourComplaintsPending"]}
                </FormHelperText>
              )}
            </div>

            {/* Labour Complaints Resolution Rate */}
            <div style={{ marginBottom: "1rem" }}>
              <p
                className={styles.form_heading}
                style={{ fontWeight: "bold", marginTop: "1rem" }}
              >
                4. Resolution Rate of Labour-Related Complaints:
              </p>
              <TextField
                name="labourComplaintsResolutionRate"
                value={formData.labourComplaintsResolutionRate}
                onChange={handleNumberChange}
                variant="outlined"
                fullWidth
                size="small"
                sx={{ pl: 2, width: "50%" }}
                placeholder="Auto-calculated"
                disabled
                error={!!errors?.["labourComplaintsResolutionRate"]}
              />
              {errors?.["labourComplaintsResolutionRate"] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.["labourComplaintsResolutionRate"]}
                </FormHelperText>
              )}
            </div>
                {console.log(formData.POSH, "formData.POSH")}
            {/* POSH Policy */}
            <BooleanRadioGroup
              label="5. Does the organization have a POSH (Prevention of Sexual Harassment) policy in place?"
              name="status"
              value={parseStatusValue(formData.POSH?.status, true)}
              onChange={(e) => handlePolicyChange(e, "POSH")}
            />
            <Box sx={{ mb: 0.5, mt: 0 }}>
              {parseStatusValue(formData.POSH?.status, true) && (
                <div
                  style={{
                    marginTop: "0.5rem",
                    marginLeft: "17px",
                  }}
                >
                  <FileUpload
                    setDocumentUrl={(file) => handleFileChange(file, "POSH")}
                    filesList={formData.POSH.documentUrl}
                    isDisabled={true}
                  />
                </div>
              )}
              {parseStatusValue(formData.POSH?.status, true) &&
                errors?.POSH?.["documentUrl"] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    marginLeft: "1rem",
                    fontSize: "14px",
                  }}
                >
                  {errors?.POSH?.["documentUrl"]}{" "}
                  {/* Customize the error text appearance */}
                </FormHelperText>
              )}
            </Box>
            {parseStatusValue(formData.POSH?.status, true) && (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <p
                    className={styles.form_heading}
                    style={{ fontWeight: "bold", marginTop: "1rem" }}
                  >
                    a. How many POSH-related cases were filed during the month :
                  </p>
                  <TextField
                    name="filed"
                    value={formData.POSH?.filed}
                    onChange={(e) => handleNumberChangePOSH(e, "POSH")}
                    onKeyDown={handleDigitOnlyKeyDown}
                    onPaste={handleDigitOnlyPaste}
                    variant="outlined"
                    // type="number"
                    fullWidth
                    size="small"
                    sx={{ pl: 2, width: "50%" }}
                    placeholder="Enter Number"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={
                      parseStatusValue(formData.POSH?.status, true) &&
                      !!errors?.POSH?.["filed"]
                    }
                  />
                  {console.log(errors?.POSH?.["filed"], "errors?.POSH?.[\"filed\"]")}
                  {parseStatusValue(formData.POSH?.status, true) &&
                    errors?.POSH?.["filed"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.POSH?.["filed"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <p
                    className={styles.form_heading}
                    style={{ fontWeight: "bold", marginTop: "1rem" }}
                  >
                    b. How many of these cases were resolved during the month :
                  </p>
                  <TextField
                    name="resolved"
                    value={formData.POSH?.resolved}
                    onChange={(e) => handleNumberChangePOSH(e, "POSH")}
                    onKeyDown={handleDigitOnlyKeyDown}
                    onPaste={handleDigitOnlyPaste}
                    variant="outlined"
                    // type="number"
                    fullWidth
                    size="small"
                    sx={{ pl: 2, width: "50%" }}
                    placeholder="Enter Number"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={
                      parseStatusValue(formData.POSH?.status, true) &&
                      !!errors?.POSH?.["resolved"]
                    }
                  />
                  {parseStatusValue(formData.POSH?.status, true) &&
                    errors?.POSH?.["resolved"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.POSH?.["resolved"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <p
                    className={styles.form_heading}
                    style={{ fontWeight: "bold", marginTop: "1rem" }}
                  >
                    c. How many cases remain pending at the end of the month :
                  </p>
                  <TextField
                    name="pending"
                    value={formData.POSH?.pending}
                    onChange={(e) => handleNumberChangePOSH(e, "POSH")}
                    onKeyDown={handleDigitOnlyKeyDown}
                    onPaste={handleDigitOnlyPaste}
                    variant="outlined"
                    // type="number"
                    fullWidth
                    size="small"
                    sx={{ pl: 2, width: "50%" }}
                    placeholder="Enter Number"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={
                      parseStatusValue(formData.POSH?.status, true) &&
                      !!errors?.POSH?.["pending"]
                    }
                  />
                  {parseStatusValue(formData.POSH?.status, true) &&
                    errors?.POSH?.["pending"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.POSH?.["pending"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Submit Button */}
        <Box
          sx={{
            width: "100%",
            mt: "10%",
            mb: 5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            sx={backButtonSx}
            onClick={() => navigate(`/social/data/community-engagement/${id}`)}
          >
            <WestIcon sx={{ mr: 2 }} /> Back
          </Button>

          <Button
            variant="contained"
            className={styles.submit_button}
            onClick={handleSubmit}
            sx={primaryButtonSx}
          >
            Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
          </Button>
        </Box>
      </div>
    </MainDashboardLayout>
  );
};

export default LabourPractices;
